import React, { forwardRef } from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { PolymorphicComponentProps, PolymorphicRef, SxProps } from '../../utils';
import { ButtonSize, ButtonVariant } from '../../model';
import { Box } from '../Box';
import { BadgeProps } from '../Badge';
import { IconButton } from '../IconButton';

type DefaultButtonRootComponent = 'button';

export type ButtonType = 'button' | 'icon';

export interface BaseButtonProps {
  Badge?: React.ReactElement<BadgeProps>;
  /**
   * Determines what type of button is rendered.
   * - 'button': A full button will be rendered. This is the default when the `children` prop is provided.
   * - 'icon': An icon-only button will be rendered. This is the default when the `children` prop is falsy.
   */
  buttonType?: ButtonType;
  /**
   * Content to be rendered inside the button. When children are falsy and icons are provided, an icon button will be
   * rendered.
   */
  children?: React.ReactNode;
  danger?: boolean;
  /**
   * Icon element placed after the children. When specified and children are falsy, this icon will be rendered inside
   * an icon button.
   */
  EndIcon?: React.ReactNode;
  fullWidth?: boolean;
  size?: ButtonSize;
  /**
   * Icon element place before the children. When specified and children are falsy, this icon will be rendered inside
   * an icon button.
   */
  StartIcon?: React.ReactNode;
  sx?: SxProps;
  /**
   * Variation of the button to render.
   */
  variant?: ButtonVariant;
}

export type ButtonProps<RootComponent extends React.ElementType = DefaultButtonRootComponent> =
  PolymorphicComponentProps<RootComponent, BaseButtonProps>;

const ButtonWithoutForwardRef = <RootComponent extends React.ElementType = DefaultButtonRootComponent>(
  {
    size = 'medium',
    variant = 'primary',
    danger = false,
    children,
    StartIcon,
    EndIcon,
    Badge,
    buttonType: initButtonType,
    component,
    ...props
  }: ButtonProps<RootComponent>,
  ref?: PolymorphicRef<RootComponent>
) => {
  // The default button type used when `buttonType` is not explicitly set is determined based on the presence of
  // children. When children are falsy, an icon button is used because there would be nothing meaningful to render
  // inside the button. Otherwise, a regular button is used.
  const defaultButtonType: ButtonType = children ? 'button' : 'icon';
  const buttonType = initButtonType || defaultButtonType;

  // Map the variant option supplied to the appropriate MUI Button props
  const variantProps = getVariantProps(danger, variant);

  const hasIcons = !!StartIcon || !!EndIcon;
  // Icon button requires at least one icon and a non-primary variant.
  const renderIconButton = buttonType === 'icon' && hasIcons && variant !== 'primary';
  if (renderIconButton) {
    return (
      <IconButton
        {...props}
        component={component ?? 'button'}
        variant={variant}
        size={size}
        danger={danger}
        Badge={Badge}
        ref={ref}
      >
        {StartIcon}
        {EndIcon}
      </IconButton>
    );
  }

  return (
    <MuiButton
      {...props}
      {...variantProps}
      component={component ?? 'button'}
      startIcon={StartIcon}
      endIcon={EndIcon}
      size={size}
      ref={ref}
    >
      {children}
      {Badge && <Box sx={{ position: 'absolute', top: '-6px', right: '-4px' }}>{Badge}</Box>}
    </MuiButton>
  );
};

// Intentionally using some `any` here to around around some type errors before the casting. We have to do some casting
// here because forwardRef, generics, and TypeScript do not play nicely together.
// https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/forward_and_create_ref/#generic-forwardrefs
// TODO: clean this up when we are able to move to React 19, which no longer requires forwardRef.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = forwardRef<any, BaseButtonProps>(ButtonWithoutForwardRef) as <
  RootComponent extends React.ElementType = DefaultButtonRootComponent,
>(
  props: ButtonProps<RootComponent> & { ref?: PolymorphicRef<RootComponent> }
) => ReturnType<typeof ButtonWithoutForwardRef>;

// The MUI Button props that we set based on the variant option passed-in
type MuiPropsFromVariant = Required<Pick<MuiButtonProps, 'variant' | 'color'>>;

function getVariantProps(danger: boolean, variant: ButtonVariant) {
  // Map from our variant options to the MUI variant options
  const variantMap: Record<ButtonVariant, MuiPropsFromVariant> = {
    primary: {
      variant: 'contained',
      color: danger ? 'error' : 'primary',
    },
    secondary: {
      variant: 'outlined',
      color: danger ? 'error' : 'secondary',
    },
    tertiary: {
      variant: 'text',
      color: danger ? 'error' : 'primary',
    },
  };

  return variantMap[variant];
}
